(function () {
  if (customElements.get('show-more')) return;

  class ShowMore extends HTMLElement {
    constructor() {
      super();
      this.attachShadow({ mode: 'open' });
      this.expanded = false;
      this.checkScheduled = false;
    }

    connectedCallback() {
      this.baseLimit = Number(this.getAttribute('limit')) || 400;
      this.limit = this.getEffectiveLimit();
      this.moreText = this.getAttribute('more-text') || 'Read more';
      this.lessText = this.getAttribute('less-text') || 'Show less';
      this.render();
      this.scheduleHeightCheck();
    }

    getEffectiveLimit() {
      const base = this.baseLimit || Number(this.getAttribute('limit')) || 400;
      if (window.matchMedia('(max-width: 768px)').matches) {
        return base; /* live parity: live keeps 400px on mobile too (was Math.min(base, 320)) */
      }
      return base;
    }

    render() {
      this.shadowRoot.innerHTML = `
        <style>
          :host {
            display: block;
            margin-bottom: 1em;
            text-align: center;
            font-family: "Crimson Text", serif;
            font-size: 17.5px;
            line-height: 1.6;
            color: #fff;
          }
          :host([data-short-content]) {
            margin-bottom: 0;
          }
          ::slotted(p),
          ::slotted(h1),
          ::slotted(h2),
          ::slotted(h3),
          ::slotted(h4),
          ::slotted(h5),
          ::slotted(h6) {
            text-align: center;
          }
          ::slotted(h6) {
            font-size: 22px;
            line-height: 1.35;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.04em;
            margin: 24px 0 16px;
            color: #fff;
          }
          ::slotted(p) {
            font-size: 17.5px;
            line-height: 1.65;
            margin: 0 0 15px;
          }
          @media screen and (max-width: 768px) {
            :host {
              font-size: 17.5px;
              line-height: 1.65;
              text-align: left;
            }
            ::slotted(p),
            ::slotted(h1),
            ::slotted(h2),
            ::slotted(h3),
            ::slotted(h4),
            ::slotted(h5),
            ::slotted(h6) {
              text-align: left;
            }
            ::slotted(h1),
            ::slotted(h2),
            ::slotted(h3),
            ::slotted(h4),
            ::slotted(h5),
            ::slotted(h6) {
              font-size: 22px;
              line-height: 1.35;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.04em;
            }
            ::slotted(p),
            ::slotted(div),
            ::slotted(span),
            ::slotted(li) {
              font-size: 17.5px;
              line-height: 1.65;
              text-align: left;
            }
            .content-wrapper {
              text-align: left;
            }
            #toggle-btn {
              margin: 15px 0 0;
              align-self: flex-start;
            }
          }
          ::slotted(p:last-child) {
            margin-bottom: 0;
          }
          ::slotted(strong),
          ::slotted(b) {
            font-weight: 700;
          }
          .content-wrapper {
            position: relative;
            overflow: hidden;
            transition: max-height 0.4s ease-in-out;
            max-height: none;
            text-align: center;
          }
          .content-wrapper.is-clamped {
            max-height: var(--show-more-limit, 400px);
          }
          .content-wrapper.expanded {
            max-height: none;
          }
          #toggle-btn {
            display: none;
            box-sizing: border-box;
            height: 44px;
            color: #000;
            background: #c29c6b;
            border-color: #c29c6b;
            font-size: 18px;
            line-height: 44px;
            cursor: pointer;
            text-decoration: none !important;
            border-radius: 0;
            border: 0;
            padding: 0 15px;
            margin: 15px auto 0;
            align-items: center;
            justify-content: center;
          }
          #toggle-btn:hover {
            background-color: #b98645;
            border-color: #b98645;
          }
        </style>
        <div class="content-wrapper" id="wrapper"><slot></slot></div>
        <a id="toggle-btn" class="cta-button">${this.moreText}</a>
      `;

      this.btn = this.shadowRoot.getElementById('toggle-btn');
      this.wrapper = this.shadowRoot.getElementById('wrapper');
      this.wrapper.style.setProperty('--show-more-limit', this.limit + 'px');
      this.btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      });
    }

    scheduleHeightCheck() {
      this.checkHeight();

      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.checkHeight());
      }

      window.addEventListener('load', () => this.checkHeight(), { once: true });
      this.onViewportChange = () => {
        this.limit = this.getEffectiveLimit();
        if (this.wrapper) {
          this.wrapper.style.setProperty('--show-more-limit', this.limit + 'px');
        }
        this.checkHeight();
      };
      window.addEventListener('resize', this.onViewportChange);
      window.addEventListener('orientationchange', this.onViewportChange);

      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => this.scheduleHeightCheckDebounced());
        Array.from(this.children).forEach((child) => this.resizeObserver.observe(child));
      }
    }

    scheduleHeightCheckDebounced() {
      if (this.expanded) return;
      if (this.checkScheduled) return;
      this.checkScheduled = true;
      requestAnimationFrame(() => {
        this.checkScheduled = false;
        this.checkHeight();
      });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.onViewportChange) {
        window.removeEventListener('resize', this.onViewportChange);
        window.removeEventListener('orientationchange', this.onViewportChange);
      }
    }

    isEmptyNode(node) {
      if (!node || node.nodeType !== Node.ELEMENT_NODE) return true;
      const text = (node.textContent || '').replace(/[\s\u00a0]/g, '');
      if (text) return false;
      return /^(P|H1|H2|H3|H4|H5|H6|DIV|SPAN)$/i.test(node.tagName);
    }

    pruneEmptyNodes() {
      Array.from(this.children).forEach((child) => {
        if (this.isEmptyNode(child)) {
          child.hidden = true;
          child.setAttribute('data-show-more-empty', '');
        } else {
          child.hidden = false;
          child.removeAttribute('data-show-more-empty');
        }
      });
    }

    measureContentHeight() {
      if (!this.wrapper) return 0;

      this.pruneEmptyNodes();

      const saved = {
        expanded: this.wrapper.classList.contains('expanded'),
        clamped: this.wrapper.classList.contains('is-clamped'),
        maxHeight: this.wrapper.style.maxHeight,
      };

      this.wrapper.classList.remove('is-clamped', 'expanded');
      this.wrapper.style.maxHeight = 'none';

      const scrollHeight = this.wrapper.scrollHeight;
      let height = scrollHeight;

      if (height <= 0) {
        height = Array.from(this.children)
          .filter((child) => !child.hasAttribute('data-show-more-empty'))
          .reduce((total, child) => {
            const style = window.getComputedStyle(child);
            return (
              total +
              child.getBoundingClientRect().height +
              (parseFloat(style.marginTop) || 0) +
              (parseFloat(style.marginBottom) || 0)
            );
          }, 0);
      }

      this.wrapper.classList.toggle('expanded', saved.expanded);
      this.wrapper.classList.toggle('is-clamped', saved.clamped);
      this.wrapper.style.maxHeight = saved.maxHeight;

      return height;
    }

    applyClampState() {
      if (!this.wrapper || !this.btn) return;

      if (this.expanded) {
        this.wrapper.classList.remove('is-clamped');
        this.wrapper.classList.add('expanded');
        this.wrapper.style.maxHeight = '';
        this.btn.textContent = this.lessText;
      } else {
        this.wrapper.classList.remove('expanded');
        this.wrapper.classList.add('is-clamped');
        this.wrapper.style.maxHeight = this.limit + 'px';
        this.btn.textContent = this.moreText;
      }
    }

    checkHeight() {
      if (!this.wrapper || !this.btn) return;

      requestAnimationFrame(() => {
        this.limit = this.getEffectiveLimit();
        this.wrapper.style.setProperty('--show-more-limit', this.limit + 'px');

        const contentHeight = this.measureContentHeight();
        const needsToggle = contentHeight > this.limit + 1;

        if (!needsToggle) {
          this.wrapper.classList.remove('is-clamped', 'expanded');
          this.wrapper.style.maxHeight = '';
          this.btn.style.display = 'none';
          this.setAttribute('data-short-content', '');
          this.expanded = false;
          return;
        }

        this.btn.style.display = 'inline-flex';
        this.removeAttribute('data-short-content');
        this.applyClampState();
      });
    }

    toggle() {
      if (this.hasAttribute('data-short-content')) return;

      this.expanded = !this.expanded;
      this.applyClampState();
    }
  }

  customElements.define('show-more', ShowMore);
})();
